'use client';
import React, { useState, useRef, useCallback } from 'react';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export default function CompressPDFTool() {
  const [file, setFile] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [result, setResult] = useState<{ originalSize: number; compressedSize: number } | null>(null);
  const [imageQuality, setImageQuality] = useState(0.6);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) { setFile(f); setResult(null); }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f?.type === 'application/pdf') { setFile(f); setResult(null); }
    else alert('Please upload a PDF file.');
  };

  const handleCompress = async () => {
    if (!file) return;
    setProcessing(true);
    setProgress(10);
    try {
      const pdfjsLib = await import('pdfjs-dist');
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
      const { PDFDocument } = await import('pdf-lib');

      setProgress(20);
      const arrayBuffer = await file.arrayBuffer();
      const srcPdf = await pdfjsLib.getDocument({ data: arrayBuffer.slice(0) }).promise;
      const newPdf = await PDFDocument.create();

      for (let i = 1; i <= srcPdf.numPages; i++) {
        setProgress(20 + Math.round((i / srcPdf.numPages) * 60));
        const page = await srcPdf.getPage(i);
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;
        await page.render({ canvasContext: ctx, viewport }).promise;
        const jpegDataUrl = canvas.toDataURL('image/jpeg', imageQuality);
        const base64 = jpegDataUrl.split(',')[1];
        const binaryStr = atob(base64);
        const bytes = new Uint8Array(binaryStr.length);
        for (let j = 0; j < binaryStr.length; j++) bytes[j] = binaryStr.charCodeAt(j);
        const embeddedImage = await newPdf.embedJpg(bytes.buffer);
        const pdfPage = newPdf.addPage([viewport.width, viewport.height]);
        pdfPage.drawImage(embeddedImage, { x: 0, y: 0, width: viewport.width, height: viewport.height });
      }

      setProgress(90);
      const pdfBytes = await newPdf.save();
      setProgress(100);

      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      setResult({ originalSize: file.size, compressedSize: blob.size });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `compressed_${file.name}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert('Compression failed. Please try again.');
    } finally {
      setProcessing(false);
      setProgress(0);
    }
  };

  const savings = result ? Math.round(((result.originalSize - result.compressedSize) / result.originalSize) * 100) : 0;

  return (
    <div className="space-y-5">
      {!file ? (
        <div
          className={`upload-zone rounded-2xl p-10 text-center cursor-pointer ${isDragging ? 'drag-over' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
          </div>
          <p className="font-semibold text-text-primary mb-1">Upload PDF</p>
          <p className="text-text-muted text-sm">Drag & drop or click to browse</p>
          <input ref={fileInputRef} type="file" accept="application/pdf" className="hidden" onChange={handleFileChange} />
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex items-center justify-between bg-surface rounded-xl p-3">
            <div>
              <p className="font-semibold text-text-primary text-sm">{file.name}</p>
              <p className="text-text-muted text-xs">{formatBytes(file.size)}</p>
            </div>
            <button onClick={() => { setFile(null); setResult(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} className="text-text-muted hover:text-red-500 transition-colors text-xs">Remove</button>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="font-semibold text-text-primary text-sm">Compression Level</label>
              <span className="text-brand-blue font-bold text-sm">
                {imageQuality >= 0.8 ? 'Low (Best Quality)' : imageQuality >= 0.5 ? 'Medium' : 'High (Smallest Size)'}
              </span>
            </div>
            <input
              type="range"
              min={0.2}
              max={0.9}
              step={0.1}
              value={imageQuality}
              onChange={(e) => { setImageQuality(Number(e.target.value)); setResult(null); }}
              className="w-full h-2 rounded-full appearance-none cursor-pointer"
              style={{ accentColor: '#2563EB' }}
            />
            <div className="flex justify-between text-xs text-text-muted mt-1"><span>Smaller file</span><span>Better quality</span></div>
          </div>

          <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-xs text-amber-700">
            ⚠️ This tool re-renders PDF pages as images. Text-based PDFs will become image-based after compression. For PDFs with selectable text, consider using a lower compression level.
          </div>

          {processing && (
            <div>
              <div className="flex justify-between text-xs text-text-muted mb-1"><span>Compressing…</span><span>{progress}%</span></div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-brand-blue rounded-full transition-all duration-300" style={{ width: `${progress}%` }} /></div>
            </div>
          )}

          {result && (
            <div className="bg-green-50 border border-green-100 rounded-2xl p-4">
              <p className="font-semibold text-green-800 mb-3">✓ Compression complete!</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white rounded-xl p-3 text-center">
                  <p className="text-text-muted text-xs mb-1">Original</p>
                  <p className="font-bold text-text-primary text-sm">{formatBytes(result.originalSize)}</p>
                </div>
                <div className="bg-white rounded-xl p-3 text-center">
                  <p className="text-text-muted text-xs mb-1">Compressed</p>
                  <p className="font-bold text-brand-blue text-sm">{formatBytes(result.compressedSize)}</p>
                </div>
                <div className="bg-white rounded-xl p-3 text-center">
                  <p className="text-text-muted text-xs mb-1">Saved</p>
                  <p className={`font-bold text-sm ${savings > 0 ? 'text-green-600' : 'text-orange-500'}`}>{savings > 0 ? `${savings}%` : 'Minimal'}</p>
                </div>
              </div>
            </div>
          )}

          <button
            onClick={handleCompress}
            disabled={processing}
            className="btn-primary w-full text-base py-3.5 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {processing ? (
              <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Compressing…</>
            ) : (
              'Compress PDF'
            )}
          </button>
        </div>
      )}
    </div>
  );
}
